/* eslint-disable max-lines */
import { Button, ButtonProps, Form, Input, Popover, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchPanelFilterFormProps } from 'src/components/SearchPanel/SearchPanelFilterForm';
import IconFilterClear from 'src/icons/filter-clear.svg?react';
import FilterIconActive from 'src/icons/icon-filter-active.svg?react';
import FilterIcon from 'src/icons/icon-filter.svg?react';
import SearchIcon from 'src/icons/icon-search.svg?react';
import { NamePath } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import styled from 'styled-components';

export type SearchPanelProps = {
    inputSearchFormName?: NamePath;
    inputSearchPlaceholder?: string;
    store: BasicStore<any>;
    renderPanel?: (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        props?: SearchPanelFilterFormProps,
    ) => React.ReactElement<SearchPanelFilterFormProps>;
};

type PopperElement = { close: () => void };

export const SearchPanel = observer(
    ({
        inputSearchFormName = 'search',
        store,
        inputSearchPlaceholder = '',
        renderPanel,
    }: SearchPanelProps) => {
        const popover = useRef<PopperElement>();
        const { t } = useTranslation();
        const [form] = Form.useForm();

        useEffect(() => {
            form.setFieldsValue(store.filterCriteria?.filter || {});
        }, [store.filterCriteria?.filter]);

        useEffect(() => {
            return () => {
                store.filterCriteria?.resetFilter();
            };
        }, []);

        return (
            <Form
                form={form}
                onFinish={(values) => {
                    const trimmedValue = {
                        ...values,
                        [inputSearchFormName]:
                            values?.[inputSearchFormName]?.trim?.(),
                    };
                    store.filterCriteria?.applyFilter(trimmedValue);
                    store.pager?.reset?.();
                    store.loadList();
                }}
                layout="vertical"
            >
                {store.filterCriteria && (
                    <StyledFormItem name={inputSearchFormName}>
                        <StyledInputSearch
                            addonBefore={
                                renderPanel && (
                                    <StyledPopper
                                        ref={(el: PopperElement) => {
                                            popover.current = el;
                                        }}
                                        content={renderPanel({
                                            form,
                                            closePopper: () =>
                                                popover.current?.close(),
                                        })}
                                        trigger="click"
                                        placement="bottomLeft"
                                        data-testid="search-panel-filter-popover"
                                    >
                                        <StyledFilterButton
                                            hasFilter={store.hasFilterValues}
                                            data-testid="search-panel-filter-filter-btn"
                                        >
                                            {store.hasFilterValues ? (
                                                <FilterIconActive data-testid="search-panel-filter-filter-btn-icon-active" />
                                            ) : (
                                                <FilterIcon data-testid="search-panel-filter-filter-btn-icon-inactive" />
                                            )}
                                        </StyledFilterButton>
                                    </StyledPopper>
                                )
                            }
                            addonAfter={
                                store.hasSearchOrFilterValues && (
                                    <Tooltip
                                        title={t(
                                            'Components.Filter.Button.Clear',
                                        )}
                                    >
                                        <StyledClearFilterButton
                                            type="link"
                                            icon={
                                                <IconFilterClear data-testid="search-panel-filter-clear-icon" />
                                            }
                                            onClick={() => {
                                                form.resetFields();
                                                store.filterCriteria?.resetFilter();
                                                store.loadList();
                                            }}
                                            data-testid="search-panel-filter-clear-button"
                                        />
                                    </Tooltip>
                                )
                            }
                            placeholder={inputSearchPlaceholder}
                            prefix={
                                <StyledSearchIcon data-testid="search-panel-filter-search-icon" />
                            }
                            onPressEnter={(e) => {
                                e.preventDefault();
                                store.pager?.reset?.();
                                form.submit();
                            }}
                            data-testid="search-panel-filter-input"
                        />
                    </StyledFormItem>
                )}
            </Form>
        );
    },
);

const StyledFormItem = styled(Form.Item)`
    margin-bottom: 0;
`;

const StyledFilterButton = styled.button<ButtonProps & { hasFilter?: boolean }>`
    border: none;
    width: 3rem;
    height: 2rem;
    font-size: 0.7rem;
    background: ${(props) =>
        props.hasFilter
            ? 'var(--filter-button-active-bg)'
            : 'var(--filter-button-bg)'};
    color: black;
    border-radius: 0.3rem;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding: 0 0.7rem;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 1.1rem;
        height: 0.9rem;
        flex-shrink: 0;
    }

    &:active,
    &:hover,
    &:focus,
    &.active {
        background: var(--filter-button-active-bg);
        svg {
            path {
                stroke: #ffffff;
            }
        }
    }
`;

const StyledInputSearch = styled(Input.Search)`
    background: transparent;

    .ant-input-group {
        display: flex;
    }

    .ant-input-search-button {
        display: none;
    }

    .ant-btn-icon-only {
        width: auto;
        height: auto;
    }

    .ant-input-group-addon {
        display: flex;
        width: fit-content;
        padding: 0;
        border: none;
        background: transparent;
    }

    .ant-input-affix-wrapper {
        background: #f5f5f7;
        border: none;
        margin-top: 0.05rem;
        height: 2rem;
        padding: 0 11px;

        border-left: 0.15rem solid white;

        border-top-left-radius: 0;
        border-bottom-left-radius: 0;

        border-top-right-radius: 0.37rem !important;
        border-bottom-right-radius: 0.37rem !important;
    }

    .ant-input-affix-wrapper-focused {
        box-shadow: none;
    }

    .ant-input {
        background: transparent;
        padding: 0;
        line-height: 1;
        height: 100%;
        font-size: 0.875rem;
    }

    .ant-input-prefix {
        margin-right: 0.5rem;
        padding: 0;
        display: flex;
        align-items: center;
    }

    .ant-input-suffix {
        margin-left: 0.5rem;
        padding: 0;
        display: flex;
        align-items: center;
    }

    .anticon.ant-input-clear-icon-hidden {
        visibility: visible;
    }

    .ant-input::placeholder {
        color: transparent;
    }

    .ant-input:focus::placeholder {
        color: #b9b9b9;
    }

    .ant-input-affix-wrapper {
        overflow: visible;
    }

    .ant-input {
        overflow: visible;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

const StyledPopper = styled(Popover)`
    .ant-popover-inner {
        border-radius: 0.35rem;
    }
    .ant-popover-arrow {
        display: none;
    }
    .ant-popover-inner {
        border-radius: 0.47rem;
    }
`;

const StyledClearFilterButton = styled(Button)`
    display: flex;
    align-items: center;
    padding: 0;
    margin-left: 0.4rem;
    height: auto;
    svg {
        width: 1rem;
        flex-shrink: 0;
    }
    &:hover {
        svg {
            circle {
                fill: #787879 !important;
            }
        }
    }
`;

const StyledSearchIcon = styled(SearchIcon)`
    width: 0.9rem;
    height: 0.9rem;
    flex-shrink: 0;
`;
